import { buildExecutionScopeDuckDbPath } from '../worker/executionScopePolicy'

const GLOBAL_BROWSER_TAB_ID_KEY = '__FG_BROWSER_TAB_ID__'
const SESSION_BROWSER_TAB_ID_KEY = '__fg_browser_tab_execution_id_v1'
const LOCAL_BROWSER_TAB_ID_KEY = '__fg_browser_tab_execution_id_v1'
const CHANNEL_NAME = 'fg-tab-execution-leases'
const HEARTBEAT_MS = 2_500
const STALE_OWNER_MS = 12_000
const ORPHAN_CLEANUP_MS = 24 * 60 * 60 * 1000

const normalizeText = (value: unknown): string => String(value ?? '').trim()

const randomToken = (): string => {
  const cryptoLike = (globalThis as any)?.crypto
  if (cryptoLike && typeof cryptoLike.randomUUID === 'function') {
    const uuid = normalizeText(cryptoLike.randomUUID())
    if (uuid) return uuid
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

let browserTabId = ''
const ownerId = `owner_${randomToken()}`
const pageExecutionSessionId = `pg_${randomToken()}`
let channel: BroadcastChannel | null = null
let lastOrphanCleanupAt = 0

type RemoteOwner = {
  ownerId: string
  browserTabId: string
  path: string
  executionScopeId: string
  lastSeenAt: number
}

type LocalLease = {
  path: string
  datasetId: string
  executionScopeId: string
  intervalId: ReturnType<typeof setInterval> | null
}

const remoteOwnersByPath = new Map<string, Map<string, RemoteOwner>>()
const localLeasesByPath = new Map<string, LocalLease>()

const assignBrowserTabId = (next: string): string => {
  const id = normalizeText(next) || `bt_${randomToken()}`
  browserTabId = id
  try {
    ;(globalThis as any)[GLOBAL_BROWSER_TAB_ID_KEY] = id
  } catch {
    // best effort only
  }
  try {
    globalThis.sessionStorage?.setItem(SESSION_BROWSER_TAB_ID_KEY, id)
  } catch {
    // best effort only
  }
  try {
    globalThis.localStorage?.setItem(LOCAL_BROWSER_TAB_ID_KEY, id)
  } catch {
    // best effort only
  }
  return id
}

export const getBrowserTabId = (): string => {
  if (browserTabId) return browserTabId
  const existing = normalizeText((globalThis as any)?.[GLOBAL_BROWSER_TAB_ID_KEY])
  const sessionExisting = (() => {
    try {
      return normalizeText(globalThis.sessionStorage?.getItem(SESSION_BROWSER_TAB_ID_KEY))
    } catch {
      return ''
    }
  })()
  const localExisting = (() => {
    try {
      return normalizeText(globalThis.localStorage?.getItem(LOCAL_BROWSER_TAB_ID_KEY))
    } catch {
      return ''
    }
  })()
  return assignBrowserTabId(existing || sessionExisting || localExisting || `bt_${randomToken()}`)
}

export const getBrowserTabExecutionId = getBrowserTabId

export const getBrowserTabExecutionSessionId = (): string => pageExecutionSessionId

export const releaseTabExecutionLeases = (): void => {
  const bc = ensureChannel()
  for (const lease of localLeasesByPath.values()) {
    if (lease.intervalId) clearInterval(lease.intervalId)
    postLeaseMessage(bc, {
      type: 'release',
      ownerId,
      browserTabId: getBrowserTabId(),
      path: lease.path,
      executionScopeId: lease.executionScopeId,
      datasetId: lease.datasetId,
      ts: Date.now(),
    })
  }
  localLeasesByPath.clear()
}

export const repairBrowserTabId = (reason?: string | null): string => {
  releaseTabExecutionLeases()
  const previousBrowserTabId = getBrowserTabId()
  const next = assignBrowserTabId(`bt_${randomToken()}`)
  try {
    console.warn('[FG-TAB-EXECUTION]', JSON.stringify({
      event: 'browser_tab_id_repaired',
      ts: Date.now(),
      reason: normalizeText(reason) || null,
      previousBrowserTabId,
      browserTabId: next,
    }))
  } catch {
    // debug logging only
  }
  return next
}

export const buildTabExecutionScopeId = (args: {
  browserTabId?: string | null
  executionSessionId?: string | null
  workbookScopeId?: string | null
  fallbackExecutionScopeId?: string | null
}): string => {
  const tabId = normalizeText(args.browserTabId) || getBrowserTabId()
  const sessionId = normalizeText(args.executionSessionId)
  const workbookScopeId = normalizeText(args.workbookScopeId)
  const fallbackExecutionScopeId = normalizeText(args.fallbackExecutionScopeId)
  const baseScopeId = workbookScopeId || fallbackExecutionScopeId
  const tabScopeId = sessionId ? `tab:${tabId}::session:${sessionId}` : `tab:${tabId}`
  return baseScopeId ? `${tabScopeId}::workbook:${baseScopeId}` : tabScopeId
}

export const buildBrowserTabExecutionScopeId = (args: {
  browserTabExecutionId?: string | null
  executionSessionId?: string | null
  workbookScopeId?: string | null
  fallbackExecutionScopeId?: string | null
}): string => buildTabExecutionScopeId({
  browserTabId: args.browserTabExecutionId,
  executionSessionId: args.executionSessionId,
  workbookScopeId: args.workbookScopeId,
  fallbackExecutionScopeId: args.fallbackExecutionScopeId,
})

const postLeaseMessage = (bc: BroadcastChannel | null, message: Record<string, unknown>): void => {
  if (!bc) return
  try { bc.postMessage(message) } catch { }
}

const pruneRemoteOwners = (): void => {
  const cutoff = Date.now() - STALE_OWNER_MS
  for (const [path, owners] of remoteOwnersByPath) {
    for (const [id, owner] of owners) {
      if (owner.lastSeenAt < cutoff) owners.delete(id)
    }
    if (owners.size <= 0) remoteOwnersByPath.delete(path)
  }
}

const recordRemoteOwner = (raw: any): void => {
  const path = normalizeText(raw?.path)
  const remoteOwnerId = normalizeText(raw?.ownerId)
  if (!path || !remoteOwnerId || remoteOwnerId === ownerId) return
  let owners = remoteOwnersByPath.get(path)
  if (!owners) {
    owners = new Map()
    remoteOwnersByPath.set(path, owners)
  }
  owners.set(remoteOwnerId, {
    ownerId: remoteOwnerId,
    browserTabId: normalizeText(raw?.browserTabId),
    path,
    executionScopeId: normalizeText(raw?.executionScopeId),
    lastSeenAt: Date.now(),
  })
}

const ensureChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null
  if (channel) return channel
  channel = new BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = (event: MessageEvent) => {
    const msg = event.data && typeof event.data === 'object' ? event.data as any : null
    if (!msg) return
    const type = normalizeText(msg.type)
    const path = normalizeText(msg.path)
    const remoteOwnerId = normalizeText(msg.ownerId)
    if (!type || !path || !remoteOwnerId || remoteOwnerId === ownerId) return
    if (type === 'release') {
      remoteOwnersByPath.get(path)?.delete(remoteOwnerId)
      return
    }
    if (type === 'probe') {
      const lease = localLeasesByPath.get(path)
      if (lease) {
        postLeaseMessage(channel, {
          type: 'claim',
          ownerId,
          browserTabId: getBrowserTabId(),
          path,
          executionScopeId: lease.executionScopeId,
          datasetId: lease.datasetId,
          ts: Date.now(),
        })
      }
      return
    }
    if (type === 'claim' || type === 'heartbeat') recordRemoteOwner(msg)
  }
  try {
    const release = () => releaseTabExecutionLeases()
    globalThis.addEventListener?.('pagehide', release, { once: true })
    globalThis.addEventListener?.('beforeunload', release, { once: true })
  } catch {
    // best effort only
  }
  return channel
}

const liveOwnersForPath = (path: string): RemoteOwner[] => {
  pruneRemoteOwners()
  const owners = remoteOwnersByPath.get(path)
  return owners ? Array.from(owners.values()) : []
}

const parseWorkbookScopeFromTabScope = (executionScopeId: string): string => {
  const text = normalizeText(executionScopeId)
  const marker = '::workbook:'
  const idx = text.indexOf(marker)
  return idx >= 0 ? text.slice(idx + marker.length).trim() : ''
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const startLeaseHeartbeat = (lease: LocalLease): void => {
  const bc = ensureChannel()
  const publish = (type: 'claim' | 'heartbeat') => postLeaseMessage(bc, {
    type,
    ownerId,
    browserTabId: getBrowserTabId(),
    path: lease.path,
    executionScopeId: lease.executionScopeId,
    datasetId: lease.datasetId,
    ts: Date.now(),
  })
  publish('claim')
  lease.intervalId = setInterval(() => publish('heartbeat'), HEARTBEAT_MS)
}

const cleanupOrphanedTabExecutionFolders = async (activePath: string): Promise<void> => {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') return
  const now = Date.now()
  if (now - lastOrphanCleanupAt < 10 * 60 * 1000) return
  lastOrphanCleanupAt = now
  try {
    const root: any = await navigator.storage.getDirectory()
    const tabDir: any = await root.getDirectoryHandle('tab_execution', { create: false }).catch(() => null)
    if (!tabDir || typeof tabDir.entries !== 'function') return
    const activeRelatives = new Set<string>([
      activePath.replace(/^opfs:\/\//, '').replace(/^\/+/, ''),
      ...Array.from(localLeasesByPath.keys()).map((path) => path.replace(/^opfs:\/\//, '').replace(/^\/+/, '')),
      ...Array.from(remoteOwnersByPath.keys()).map((path) => path.replace(/^opfs:\/\//, '').replace(/^\/+/, '')),
    ])
    for await (const [scopeDirName, scopeDir] of tabDir.entries()) {
      if (!scopeDir || scopeDir.kind !== 'directory' || typeof scopeDir.entries !== 'function') continue
      let newestFileMs = 0
      let hasActiveFile = false
      for await (const [fileName, entry] of scopeDir.entries()) {
        const relative = `tab_execution/${scopeDirName}/${fileName}`
        if (activeRelatives.has(relative)) {
          hasActiveFile = true
          break
        }
        if (entry?.kind !== 'file' || typeof entry.getFile !== 'function') continue
        const file = await entry.getFile().catch(() => null)
        newestFileMs = Math.max(newestFileMs, Number(file?.lastModified ?? 0) || 0)
      }
      if (hasActiveFile) continue
      if (newestFileMs > 0 && now - newestFileMs < ORPHAN_CLEANUP_MS) continue
      await tabDir.removeEntry(scopeDirName, { recursive: true }).catch(() => { })
    }
  } catch {
    // best-effort cleanup only
  }
}

export const claimTabExecutionLease = async (args: {
  datasetId?: string | null
  workbookScopeId?: string | null
  fallbackExecutionScopeId?: string | null
}): Promise<{
  browserTabId: string
  tabExecutionScopeId: string
  opfsPath: string | null
  repaired: boolean
}> => {
  const datasetId = normalizeText(args.datasetId)
  const workbookScopeId = normalizeText(args.workbookScopeId)
  const fallbackExecutionScopeId = normalizeText(args.fallbackExecutionScopeId)
  let repaired = false
  let tabExecutionScopeId = ''
  let opfsPath = ''
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const tabId = getBrowserTabId()
    tabExecutionScopeId = buildTabExecutionScopeId({
      browserTabId: tabId,
      workbookScopeId,
      fallbackExecutionScopeId,
    })
    opfsPath = datasetId ? buildExecutionScopeDuckDbPath(datasetId, tabExecutionScopeId) : ''
    if (!opfsPath) break
    const existing = localLeasesByPath.get(opfsPath)
    if (existing && existing.executionScopeId === tabExecutionScopeId) break
    const bc = ensureChannel()
    postLeaseMessage(bc, {
      type: 'probe',
      ownerId,
      browserTabId: tabId,
      path: opfsPath,
      executionScopeId: tabExecutionScopeId,
      datasetId,
      ts: Date.now(),
    })
    if (bc) await sleep(120)
    const conflicts = liveOwnersForPath(opfsPath)
    if (conflicts.length > 0 && attempt === 0) {
      repaired = true
      try {
        console.warn('[FG-TAB-EXECUTION]', JSON.stringify({
          event: 'duplicate_path_detected',
          ts: Date.now(),
          browserTabId: tabId,
          datasetId,
          workbookScopeId: workbookScopeId || fallbackExecutionScopeId || null,
          tabExecutionScopeId,
          opfsPath,
          owners: conflicts.map((owner) => ({
            browserTabId: owner.browserTabId || null,
            executionScopeId: owner.executionScopeId || null,
            lastSeenAgeMs: Math.max(0, Date.now() - owner.lastSeenAt),
          })).slice(0, 8),
        }))
      } catch { }
      repairBrowserTabId('duplicate_path')
      continue
    }
    break
  }
  if (opfsPath && !localLeasesByPath.has(opfsPath)) {
    const lease: LocalLease = {
      path: opfsPath,
      datasetId,
      executionScopeId: tabExecutionScopeId,
      intervalId: null,
    }
    localLeasesByPath.set(opfsPath, lease)
    startLeaseHeartbeat(lease)
    void cleanupOrphanedTabExecutionFolders(opfsPath)
  }
  return {
    browserTabId: getBrowserTabId(),
    tabExecutionScopeId,
    opfsPath: opfsPath || null,
    repaired,
  }
}

export const ensureTabExecutionLeaseForScope = async (args: {
  datasetId?: string | null
  executionScopeId?: string | null
}): Promise<{
  executionScopeId: string
  browserTabId: string | null
  opfsPath: string | null
  repaired: boolean
}> => {
  const executionScopeId = normalizeText(args.executionScopeId)
  const datasetId = normalizeText(args.datasetId)
  if (!executionScopeId || !datasetId || !executionScopeId.startsWith('tab:')) {
    return { executionScopeId, browserTabId: null, opfsPath: null, repaired: false }
  }
  const workbookScopeId = parseWorkbookScopeFromTabScope(executionScopeId)
  const claimed = await claimTabExecutionLease({
    datasetId,
    workbookScopeId,
    fallbackExecutionScopeId: workbookScopeId ? null : executionScopeId,
  })
  return {
    executionScopeId: claimed.tabExecutionScopeId || executionScopeId,
    browserTabId: claimed.browserTabId,
    opfsPath: claimed.opfsPath,
    repaired: claimed.repaired,
  }
}
